import React from "react";
import { Box, Input, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { COMPANY_WIDE } from "../../suppliersMock";

const LIME_FOCUS = {
  _hover: { borderColor: "gray.400" },
  _focus: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
  _focusVisible: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
};

const fmtTotal = (n) =>
  n === 0 ? "0" : Number(n).toLocaleString("en-GB", { maximumFractionDigits: 2 });

// Emissions entry table. Divisions run down the ROWS (there may be many) and
// the fixed categories (e.g. Stationary / Mobile / Refrigerants) run across the
// COLUMNS. Values are tCO₂e. `values` is a nested map keyed by category →
// division name; onChange(category, division, value).
function EmissionsByDivisionTable({ title, description, categories, divisions, values, onChange }) {
  const rows = divisions.length > 0 ? divisions.map((d) => d.name) : [COMPANY_WIDE];

  const cellValue = (category, row) => values?.[category]?.[row] ?? "";
  const rowTotal = (row) =>
    categories.reduce((acc, cat) => acc + (Number(values?.[cat]?.[row]) || 0), 0);
  const colTotal = (category) =>
    rows.reduce((acc, row) => acc + (Number(values?.[category]?.[row]) || 0), 0);
  const grandTotal = categories.reduce((acc, cat) => acc + colTotal(cat), 0);

  return (
    <Box>
      <Text fontSize="body.l" fontWeight="700" color="darkTeal">{title}</Text>
      {description && <Text fontSize="body.xs" color="gray.500" mb={4}>{description}</Text>}

      <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white" overflowX="auto">
        <Table size="sm" variant="unstyled" minW={`${220 + categories.length * 160}px`}>
          <Thead>
            <Tr bg="gray.50">
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Division</Th>
              {categories.map((cat) => (
                <Th key={cat} py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">{cat}</Th>
              ))}
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Total (tCO₂e)</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row, idx) => (
              <Tr key={row} borderTop={idx === 0 ? "none" : "1px solid"} borderColor="gray.100">
                <Td py={2} px={4} fontSize="14px" color="gray.700" fontWeight="500">{row}</Td>
                {categories.map((cat) => (
                  <Td key={cat} py={2} px={3} textAlign="right">
                    <Input
                      type="number"
                      size="sm"
                      textAlign="right"
                      value={cellValue(cat, row)}
                      onChange={(e) => onChange(cat, row, e.target.value)}
                      placeholder="0"
                      maxW="140px"
                      ml="auto"
                      {...LIME_FOCUS}
                    />
                  </Td>
                ))}
                <Td py={2} px={4} fontSize="14px" color="gray.700" fontWeight="600" textAlign="right">{fmtTotal(rowTotal(row))}</Td>
              </Tr>
            ))}
            <Tr bg="lightLime" borderTop="2px solid" borderColor="gray.200">
              <Td py={3} px={4} fontSize="13px" color="darkTeal" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em">Total</Td>
              {categories.map((cat) => (
                <Td key={cat} py={3} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">{fmtTotal(colTotal(cat))}</Td>
              ))}
              <Td py={3} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">{fmtTotal(grandTotal)}</Td>
            </Tr>
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

export default EmissionsByDivisionTable;
